import type {
  ScoringExplain,
  TrackA,
  TrackAEntity,
} from "@/core/customers/entities/ScoringExplain";

// DTOs mirror the backend ScoringExplainResponse (docs/openapi.json). Private to details/.

interface TrackAEntityDto {
  code?: string | null;
  name: string;
  label?: string | null;
  level?: string | null;
  points?: number | null;
  unknown_code?: boolean | null;
  applicability?: boolean | null;
}

interface TrackADto {
  weight?: number | null;
  entities?: TrackAEntityDto[] | null;
  raw_score?: number | null;
  denominator?: number | null;
  /** Borne haute de l'echelle (RF=1 / RM=2 / RE=3). A ne pas confondre avec `denominator`. */
  scale_max?: number | null;
  classification?: string | null;
  hard_overrides?: string[] | null;
  completeness_pct?: number | null;
}

interface FactorsJsonDto {
  track_a?: TrackADto | null;
  // track_b / compositor / scoring_version are present in the contract but unused in v1.
}

export interface ScoringExplainDto {
  scoringEventId: string;
  businessKey: string;
  trigger?: string | null;
  scoredAt?: string | null;
  scoredBy?: string | null;
  trackAScore?: number | null;
  trackBScore?: number | null;
  combinedScore?: number | null;
  riskClass?: string | null;
  decision?: string | null;
  completenessPct?: number | null;
  errorReason?: string | null;
  factorsJson?: FactorsJsonDto | null;
}

function mapTrackAEntity(dto: TrackAEntityDto): TrackAEntity {
  return {
    code: dto.code ?? null,
    name: dto.name,
    label: dto.label ?? null,
    level: dto.level ?? null,
    points: dto.points ?? 0,
    unknownCode: dto.unknown_code ?? false,
    applicability: dto.applicability ?? false,
  };
}

function mapTrackA(dto: TrackADto): TrackA {
  return {
    weight: dto.weight ?? null,
    entities: (dto.entities ?? []).map(mapTrackAEntity),
    rawScore: dto.raw_score ?? null,
    denominator: dto.denominator ?? null,
    scaleMax: dto.scale_max ?? null,
    classification: dto.classification ?? null,
    hardOverrides: dto.hard_overrides ?? [],
    completenessPct: dto.completeness_pct ?? null,
  };
}

export function mapScoringExplainDto(dto: ScoringExplainDto): ScoringExplain {
  const trackADto = dto.factorsJson?.track_a;
  return {
    scoringEventId: dto.scoringEventId,
    businessKey: dto.businessKey,
    trigger: dto.trigger ?? null,
    scoredAt: dto.scoredAt ?? null,
    scoredBy: dto.scoredBy ?? null,
    trackAScore: dto.trackAScore ?? null,
    trackBScore: dto.trackBScore ?? null,
    combinedScore: dto.combinedScore ?? null,
    riskClass: dto.riskClass ?? null,
    decision: dto.decision ?? null,
    completenessPct: dto.completenessPct ?? null,
    errorReason: dto.errorReason ?? null,
    trackA: trackADto ? mapTrackA(trackADto) : null,
  };
}
